/**
 * RedefinirSenhaPage — define nova senha via token de reset (B1.5 spec §2.5).
 *
 * URL: /cliente/redefinir-senha/:token
 *
 * Backend: POST /api/v1/cliente/redefinir-senha/:token
 *   - 200 → senha atualizada + Set-Cookie auto-login (similar aceitar-convite)
 *   - 404 → token-invalido-ou-expirado
 *   - 400 → senha fraca / payload invalid
 *
 * Pós-sucesso → refreshSession + navigate /cliente/dashboard.
 *
 * NÃO REVERTER:
 *   - Auto-login pós-redefine (UX — não pede pra logar de novo)
 *   - Token expira em 1h (configurado server-side via env)
 *   - Backend marca token como `used_at` — não dá pra reusar
 *   - Validação client-side mínima — backend é fonte da verdade
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Button, Input, Label } from '@/components/ui';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import { clienteApi, ClienteApiError } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

export function RedefinirSenhaPage() {
  const { token } = useParams<{ token: string }>();
  const { refreshSession } = useClienteAuth();
  const navigate = useNavigate();
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!token) {
      setErrorMsg('Esse link não é válido. Peça um novo em "Esqueci minha senha".');
      return;
    }
    if (senha.length < 8) {
      setErrorMsg('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (senha !== confirmacao) {
      setErrorMsg('As duas senhas digitadas não são iguais.');
      return;
    }
    setLoading(true);
    try {
      await clienteApi.post(`/redefinir-senha/${encodeURIComponent(token)}`, {
        nova_senha: senha,
      });
      // Backend setou cookie auto-login. Atualiza contexto + redirect.
      await refreshSession();
      navigate('/cliente/dashboard');
    } catch (err) {
      // O texto do erro do servidor NUNCA vai pra tela (era `Erro: ${err.message}`
      // — o cliente lia "HTTP 500" ou o nome de uma exceção).
      if (err instanceof ClienteApiError && err.status === 404) {
        setErrorMsg(
          'Este link já foi usado ou passou de 1 hora. Peça um novo em "Esqueci minha senha".',
        );
      } else if (err instanceof ClienteApiError && err.status === 400) {
        setErrorMsg('Essa senha é fácil demais. Use ao menos 8 caracteres com letras e números.');
      } else {
        setErrorMsg(mensagemErro(err, 'Não conseguimos salvar a nova senha agora. Tente de novo.'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto pt-8" data-testid="cliente-redefinir-page">
      <div className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg p-8 shadow-sm">
        <h1 className="mb-2 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          Criar uma nova senha
        </h1>
        <p className="mb-6 text-sm text-ink-2 dark:text-ink-dark-2">
          Escolha a senha que você vai usar para entrar no Portal IGM.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="senha" required>
              Nova senha
            </Label>
            <Input
              id="senha"
              type="password"
              autoComplete="new-password"
              autoFocus
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              minLength={8}
              disabled={loading}
              data-testid="redefinir-senha-input"
            />
            <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
              Pelo menos 8 caracteres, com letras e números.
            </p>
          </div>
          <div>
            <Label htmlFor="confirmacao" required>
              Repita a senha
            </Label>
            <Input
              id="confirmacao"
              type="password"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              required
              disabled={loading}
              data-testid="redefinir-confirmacao-input"
            />
          </div>

          {errorMsg && (
            <Alert variant="danger" data-testid="redefinir-error">
              {errorMsg}
            </Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="w-full"
            data-testid="redefinir-submit-btn"
          >
            {loading ? 'Salvando…' : 'Salvar e entrar'}
          </Button>
        </form>
      </div>
    </div>
  );
}
